import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

const EmailVerification = () => {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");
      
      if (!token) {
        setStatus('error');
        setError("Invalid verification link.");
        return;
      }
    
      // Add this check to prevent duplicate requests
      if (status !== 'verifying') {
        return;
      }
    
      try {
        const response = await fetch(`/api/verify-email?token=${token}`);
        const data = await response.json();
        
        if (response.ok) {
          setStatus('success');
        } else {
          setStatus('error');
          setError(data.detail || "Verification failed. Please try again.");
        }
      } catch (err) {
        setStatus('error');
        setError("Server error. Please try again later.");
      }
    };

    verifyEmail();
  }, [searchParams]);

  const getContent = () => {
    switch (status) {
      case 'verifying':
        return {
          icon: <Loader className="animate-spin" size={64} style={{ color: theme.buttonBg }} />,
          title: "Verifying Your Email",
          message: "Please wait while we verify your email address..."
        };
      case 'success':
        return {
          icon: <CheckCircle size={64} style={{ color: '#22c55e' }} />,
          title: "Email Verified!",
          message: "Your email has been successfully verified. You can now log in to your account."
        };
      case 'error':
        return {
          icon: <XCircle size={64} style={{ color: '#ef4444' }} />,
          title: "Verification Failed",
          message: error
        };
      default:
        return null;
    }
  };

  const content = getContent();

  return (
    <div 
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ backgroundColor: theme.mainPanelBg }}
    >
      {/* Animated Background Overlay */}
      <div className="absolute inset-0 opacity-20 pointer-events-none" 
        style={{ background: `radial-gradient(circle, ${theme.sidebarBg} 20%, transparent 80%)` }}
      />
      
      {/* Content Section */}
      <div className="flex-1 flex flex-col items-center justify-center text-center p-6 relative z-10">
        <div className="mb-8">
          {content.icon}
        </div>
        
        <h1 
          className="text-4xl font-extrabold tracking-wide mb-4"
          style={{ color: theme.mainPanelText }}
        >
          {content.title}
        </h1>
        
        <div 
          className="bg-opacity-50 rounded-xl p-8 mb-8 max-w-xl"
          style={{ backgroundColor: theme.sidebarBg }}
        >
          <p 
            className="text-lg"
            style={{ color: theme.mainPanelText }}
          >
            {content.message}
          </p>
        </div>

        {status !== 'verifying' && (
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 rounded-lg font-semibold shadow-md transform hover:scale-105 transition-transform"
            style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
          >
            {status === 'success' ? 'Continue to Login' : 'Try Again'}
          </button>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;